import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/layout/PageHeader';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import Button from '../../components/ui/Button';
import Textarea from '../../components/ui/Textarea';
import { fmtDate } from '../../components/requests/RequestActions';
import { hideWorkflowNotification, listWorkflowNotifications } from '../../api/workflowNotifications';
import { WORKFLOW_NOTIFICATIONS_QUERY_KEY } from '../../hooks/useWorkflowNotificationCount';
import { pushToast } from '../../components/ui/toastBus';

function NotificationCard({ n, onHidden }) {
  const [hiding, setHiding] = useState(false);
  const [remark, setRemark] = useState('');
  const mutation = useMutation({
    mutationFn: () => hideWorkflowNotification(n.id, remark.trim()),
    onSuccess: () => {
      pushToast({ type: 'success', message: 'Notification hidden.' });
      onHidden();
    },
    onError: (err) => pushToast({ type: 'error', message: err.response?.data?.message || 'Could not hide notification.' }),
  });

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-gray-800">
            {n.candidate ? (
              <Link to={`/candidates/${n.candidate.id}`} className="font-medium text-primary hover:underline">
                {n.candidate.full_name}
              </Link>
            ) : (
              'Candidate'
            )}{' '}
            <span className="text-xs text-gray-400">{n.candidate?.reference_no}</span>
          </p>
          <p className="mt-1 text-sm text-gray-700">
            <span className="font-medium">{n.step_title}</span> — {n.field_label}:{' '}
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800">{n.answer}</span>
          </p>
          <p className="mt-1 text-xs text-gray-400">{fmtDate(n.created_at)}</p>
        </div>
        {!n.hidden_at && !hiding && (
          <Button variant="secondary" className="px-3 py-1.5 text-xs" onClick={() => setHiding(true)}>
            Hide
          </Button>
        )}
      </div>

      {n.hidden_at && (
        <p className="mt-2 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
          Hidden {fmtDate(n.hidden_at)}
          {n.hidden_remark ? ` — “${n.hidden_remark}”` : ' — no remark'}
        </p>
      )}

      {hiding && (
        <div className="mt-3 space-y-2">
          <Textarea label="Remark (optional)" value={remark} onChange={(e) => setRemark(e.target.value)} />
          <div className="flex gap-2">
            <Button className="px-3 py-1.5 text-xs" onClick={() => mutation.mutate()} loading={mutation.isPending}>
              Hide notification
            </Button>
            <Button variant="secondary" className="px-3 py-1.5 text-xs" onClick={() => setHiding(false)} disabled={mutation.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkflowNotifications() {
  const [status, setStatus] = useState('active');
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: [...WORKFLOW_NOTIFICATIONS_QUERY_KEY, 'list', status],
    queryFn: () => listWorkflowNotifications(status),
  });
  const rows = query.data || [];

  return (
    <div>
      <PageHeader
        title="Workflow Notifications"
        description="Alerts from steps you submitted where a flagged answer was selected."
      />
      <div className="mb-4 flex gap-2">
        {[
          ['active', 'Active'],
          ['hidden', 'Hidden'],
        ].map(([value, label]) => (
          <button
            key={value}
            type="button"
            onClick={() => setStatus(value)}
            className={`rounded-md px-3 py-1.5 text-sm ${status === value ? 'bg-primary text-white' : 'border border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            {label}
          </button>
        ))}
      </div>
      {query.isLoading ? (
        <div className="flex justify-center py-16">
          <Spinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={status === 'active' ? 'No notifications' : 'Nothing hidden'}
          message={status === 'active' ? 'You have no workflow notifications.' : 'No notifications have been hidden.'}
        />
      ) : (
        <div className="space-y-3">
          {rows.map((n) => (
            <NotificationCard key={n.id} n={n} onHidden={() => queryClient.invalidateQueries({ queryKey: WORKFLOW_NOTIFICATIONS_QUERY_KEY })} />
          ))}
        </div>
      )}
    </div>
  );
}
